// The Requisitions board's column for a row (V1-INTERACTIONS §4.2 board; UAT F-5). A row's stage chip is the server's
// projection (requisition.py `_stage`): a column name, or one of the three intake labels it returns before posting is
// past, which the board draws under Intake. Any other label falls to the last column of the list, so a listed
// requisition is never dropped from the board. The V1 Requisitions list groups by it; D-11's Mission Control list
// groups by its stations below, through the same function.

/** The labels `_stage` returns while the requisition is at intake. */
const INTAKE_LABELS: readonly string[] = ["Intake pending", "Intake finalized — sourcing cleared", "Panel reopened"];

/** The column a stage label is drawn in: the label when it is a column, Intake for an intake label, else the last column. */
export function boardColumnOf(label: string, columns: readonly string[]): string {
  const column = INTAKE_LABELS.includes(label) ? "Intake" : label;
  return columns.includes(column) ? column : (columns[columns.length - 1] ?? column);
}

// ---- the Mission Control list's board (D-11): the seven DC-30 stations ----------------------------------------------

/** DC-30's stations of §4.3 in order (Request · Approval · Posting · Intake · Panel · Offer · Filled): the board's columns. */
export const STATIONS = ["request", "approval", "posting", "intake", "panel", "offer", "filled"] as const;
export type Station = (typeof STATIONS)[number];

/**
 * The station a stage label stands at: the one the requisition's own track draws active (RequisitionTimeline.trackOf —
 * the first station not yet reached), so the board and the record agree. Intake pending: the intake call and the panel
 * are still to come; a reopened panel is Panel again; a finalized intake, sourcing, the screens, the interviews and the
 * debrief all wait on the Offer; an offer out, and an offer accepted while the HRIS hire is still to come (CR-034),
 * wait on Filled, the last station, where a filled or closed requisition also sits (Archive and Cancel are events on
 * the track, never stations). The stage chip is the server's projection (requisition.py `_stage` and STAGE_LABEL);
 * STAGE_LABEL's Intake and Sourcing, which `_stage` never returns, are mapped too, so no label it holds falls through.
 */
const STAGE_STATION: Readonly<Record<string, Station>> = {
  Intake: "intake",
  "Intake pending": "intake",
  "Panel reopened": "panel",
  "Intake finalized — sourcing cleared": "offer",
  Sourcing: "offer",
  Screening: "offer",
  "HM Interview": "offer",
  "Onsite Interviews": "offer",
  Debrief: "offer",
  Offer: "filled",
  "Offer Accepted": "filled",
  Filled: "filled",
  Closed: "filled",
};

/**
 * A listed requisition's station from its stage label and status: not yet posted (`draft`) is Posting; any other row
 * goes through boardColumnOf over the stations, so a label the table does not know still lands in the last station
 * and no listed requisition is dropped from the board.
 */
export function stationOf(stage: string, status: string): Station {
  if (status === "draft") return "posting";
  return boardColumnOf(STAGE_STATION[stage] ?? stage, STATIONS) as Station;
}
